import { UISimulation } from '@/components/help/UISimulation';
import { SimShell } from '@/components/help/simulations/simShell';

// PhotoGlyph is a tiny token-only camera/photo mark reused across the frames so
// the simulation never loads a real image (it stays within the help size
// budget and themes with the dashboard tokens).
function PhotoGlyph({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2"
        className="stroke-current"
        strokeWidth="1.5"
      />
      <circle cx="8.5" cy="10" r="1.5" className="fill-current" />
      <path
        d="M4 17l4.5-4.5 3 3L16 11l4 4.5"
        className="stroke-current"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Frame 1 — an image is dragged over the empty [Image] block's upload zone.
function DragFrame(): JSX.Element {
  return (
    <SimShell step={1} total={3} title="Drag an image in">
      <div className="rounded-lg border-2 border-dashed border-accent bg-accent/5 px-4 py-6 text-center">
        <PhotoGlyph className="mx-auto h-7 w-7 text-accent" />
        <div className="mt-2 text-xs font-medium text-accent">
          Drop image to upload
        </div>
        <div className="mt-1 text-[10px] font-mono text-dash-muted">
          JPEG, PNG, or GIF · up to 10 MB
        </div>
      </div>
    </SimShell>
  );
}

// Frame 2 — the file uploads; the backend optimizes it while the XHR progress
// bar fills.
function ProgressFrame(): JSX.Element {
  return (
    <SimShell step={2} total={3} title="Upload + optimize">
      <div className="rounded-lg border border-dash-border bg-dash-surface px-3 py-2.5">
        <div className="flex items-center gap-2">
          <PhotoGlyph className="h-5 w-5 text-dash-secondary" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs text-dash-primary">
              pharmacy-counter.jpg
            </div>
            <div className="text-[10px] font-mono text-dash-muted">
              1.8 MB · optimizing
            </div>
          </div>
          <span className="text-[10px] font-mono text-accent">62%</span>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded bg-dash-bg">
          <div className="h-full w-[62%] bg-accent/70" />
        </div>
      </div>
    </SimShell>
  );
}

// Frame 3 — the optimized image lands inline with the alt-text field (still
// empty, so the amber publish-gate hint shows) plus caption, credit, and width.
function FilledFrame(): JSX.Element {
  return (
    <SimShell step={3} total={3} title="Inline, awaiting alt text">
      <div className="rounded-lg border border-accent/40 bg-accent/5 p-2.5">
        <div className="flex h-16 items-center justify-center rounded bg-dash-bg">
          <PhotoGlyph className="h-7 w-7 text-dash-muted" />
        </div>
        <div className="mt-2">
          <div className="rounded border border-warning/40 bg-dash-bg px-2 py-1 text-[11px] text-dash-muted">
            Describe this image…
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-wide text-warning">
            Add alt text before publish
          </div>
        </div>
        <div className="mt-2 flex gap-1 text-[10px] font-mono">
          {['narrow', 'full', 'wide'].map((w) => (
            <span
              key={w}
              className={
                w === 'full'
                  ? 'rounded border border-accent/40 bg-accent/15 px-2 py-0.5 text-accent'
                  : 'rounded border border-dash-border px-2 py-0.5 text-dash-muted'
              }
            >
              {w}
            </span>
          ))}
        </div>
      </div>
    </SimShell>
  );
}

// ImageUploadSimulation walks through the inline upload flow: drag an image
// onto the [Image] block, watch it upload + optimize, then fill in alt text on
// the inline card. Manual Prev/Next stepping via UISimulation.
export function ImageUploadSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <DragFrame />, label: 'Drag an image onto the block' },
        { frame: <ProgressFrame />, label: 'It uploads and is optimized' },
        { frame: <FilledFrame />, label: 'Image lands inline — add alt text' },
      ]}
    />
  );
}
